import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexGrid,
  ApexLegend,
  ApexMarkers,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  NgApexchartsModule,
} from 'ng-apexcharts';

@Component({
  selector: 'finance-pwa-overall-progress-chart',
  standalone: true,
  imports: [NgApexchartsModule],
  template: `
    <apx-chart
      [series]="series"
      [chart]="chart"
      [xaxis]="xaxis"
      [yaxis]="yaxis"
      [grid]="grid"
      [stroke]="stroke"
      [markers]="markers"
      [legend]="legend"
      [tooltip]="tooltip"
      [dataLabels]="dataLabels"
    ></apx-chart>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverallProgressChartComponent {
  @Input() set categories(values: string[] | null) {
    this.xaxis = {
      ...this.xaxis,
      categories: values?.length ? values : this.defaultCategories,
    };
  }

  @Input() set actual(values: number[] | null) {
    this.series = [
      { name: 'Actual Progress', data: values?.length ? values : [0] },
      { name: '2-Year Target', data: this.targetValues },
    ];
  }

  @Input() set target(values: number[] | null) {
    this.targetValues = values?.length ? values : [0];
    this.series = [
      { name: 'Actual Progress', data: this.series[0].data as number[] },
      { name: '2-Year Target', data: this.targetValues },
    ];
  }

  private targetValues: number[] = [0];
  private readonly defaultCategories = Array.from({ length: 24 }, (_, index) => `M${index + 1}`);

  series: ApexAxisChartSeries = [
    { name: 'Actual Progress', data: [0] },
    { name: '2-Year Target', data: [0] },
  ];

  chart: ApexChart = {
    type: 'line',
    height: 290,
    toolbar: { show: false },
    zoom: { enabled: false },
  };

  stroke: ApexStroke = {
    curve: 'smooth',
    width: [3, 2],
    dashArray: [0, 6],
  };

  markers: ApexMarkers = {
    size: 0,
    hover: {
      size: 5,
    },
  };

  xaxis: ApexXAxis = {
    categories: this.defaultCategories,
    labels: {
      formatter: (value, _timestamp, options) => {
        const month = (options?.dataPointIndex ?? -1) + 1;
        if (month > 0 && month % 6 === 0) {
          return typeof value === 'string' && value.length > 0 ? value : `M${month}`;
        }

        return '';
      },
      style: {
        colors: '#40484c',
        fontFamily: 'Inter',
        fontSize: '11px',
      },
    },
    axisTicks: { show: false },
    axisBorder: { show: false },
  };

  yaxis: ApexYAxis = {
    tickAmount: 4,
    forceNiceScale: true,
    labels: {
      formatter: (value) => this.formatCompactPounds(value),
      style: {
        colors: '#40484c',
        fontFamily: 'Inter',
      },
    },
  };

  grid: ApexGrid = {
    borderColor: '#dde3e8',
    strokeDashArray: 4,
  };

  legend: ApexLegend = {
    position: 'top',
    horizontalAlign: 'left',
  };

  tooltip: ApexTooltip = {
    y: {
      formatter: (value) => this.formatCompactPounds(value),
    },
  };

  dataLabels: ApexDataLabels = {
    enabled: false,
  };

  private formatCompactPounds(value: number): string {
    const rounded = Math.round(value);
    if (Math.abs(rounded) < 1000) {
      return `£${rounded}`;
    }

    const compact = rounded / 1000;
    const formatted = Number.isInteger(compact)
      ? compact.toString()
      : compact.toFixed(1).replace(/\.0$/, '');

    return `£${formatted}k`;
  }
}
